import { useTableBulkActions } from '@monorepo/shared/componentsV2/Table/useTableBulkActions';
import { useToast } from '@monorepo/shared/contexts';
import { useModal } from '@monorepo/shared/hooks/useModalV2';
import { BulkDeleteResponse } from 'mapistry-shared';
import React, { useCallback, useState } from 'react';

type UseHandleBulkDeleteProps = {
  // More likely this callback will use RQ mutator, and it returns either result or undefined if request fails.
  // This hook expects RQ mutator to throw in case of an error, but TS type will still remain the same.
  // If RQ mutator returns undefined we throw here, so we don't need to do that in each consumer of this hook.
  onBulkDelete: () => Promise<BulkDeleteResponse | undefined>;
  pluralEntityLabel: string;
};
export function useHandleBulkDelete({
  onBulkDelete,
  pluralEntityLabel,
}: UseHandleBulkDeleteProps) {
  const { selectedCount, setSelectedIds } = useTableBulkActions();
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const {
    showUserFriendlyErrorToast,
    success: showSuccess,
    error: showError,
  } = useToast();

  // function passed to confirm modal to be executed when the confirm button is clicked
  const handleDeleteConfirm = useCallback(
    async (isConfirmed: boolean) => {
      if (isConfirmed) {
        setIsBulkDeleting(true);
        const result = await onBulkDelete();
        if (!result) {
          throw new Error(
            'Bulk delete result is undefined, it can happen if BE endpoint signature changes or if request failed and we forgot to pass "throwOnError: true" option to a deleter hook',
          );
        }
        if (result.deleteStatus === 'success') {
          showSuccess(result.deleteMessage);
        } else {
          showError(result.deleteMessage, { dontAutoHide: true });
        }
        setSelectedIds(result.idsWithValidationErrors);
      }
    },
    [onBulkDelete, setSelectedIds, showError, showSuccess],
  );

  const { confirm } = useModal();
  const handleBulkDeleteClick = useCallback(async () => {
    if (!selectedCount || isBulkDeleting) {
      return;
    }
    try {
      await confirm({
        title: `Delete ${pluralEntityLabel}`,
        description: (
          <>
            Are you sure you want to delete <b>{selectedCount}</b>{' '}
            {pluralEntityLabel}?
          </>
        ),
        cancelButtonText: `Keep ${pluralEntityLabel}`,
        confirmButtonText: `Delete ${pluralEntityLabel}`,
        afterConfirmButtonText: 'Deleting...',
        danger: true,
        onConfirmAsync: handleDeleteConfirm,
      });
    } catch (err) {
      showUserFriendlyErrorToast(
        err,
        `Unable to delete ${pluralEntityLabel}.`,
        {
          dontAutoHide: true,
        },
      );
    } finally {
      setIsBulkDeleting(false);
    }
  }, [
    confirm,
    handleDeleteConfirm,
    isBulkDeleting,
    pluralEntityLabel,
    selectedCount,
    setIsBulkDeleting,
    showUserFriendlyErrorToast,
  ]);

  return {
    handleBulkDeleteClick,
    isBulkDeleting,
  };
}
